// A three-way merge preserves independent edits and intentional removals.
const identityKeys: Record<string, string> = {
  training_records: 'id', weight_records: 'date', daily_health_records: 'date',
  diet_plans: 'id', custom_training_plans: 'id', cycle_compare_photos_v2: 'id',
};
const sets = new Set(['injury_recovery_completed_dates', 'aerobic_completed_dates']);
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

function mergeFields(base: unknown, local: unknown, remote: unknown): unknown {
  if (equal(local, base)) return remote;
  if (equal(remote, base) || equal(local, remote)) return local;
  if (!object(local) || !object(remote)) return local;
  const before = object(base) ? base : {};
  const result: Record<string, unknown> = {};
  for (const key of new Set([...Object.keys(before), ...Object.keys(local), ...Object.keys(remote)])) {
    const value = mergeFields(before[key], local[key], remote[key]);
    if (value !== undefined) result[key] = value;
  }
  return result;
}

function mergeArray(base: unknown[], local: unknown[], remote: unknown[], identity: string): unknown[] {
  const key = (v: unknown) => object(v) && v[identity] != null ? String(v[identity]) : JSON.stringify(v);
  const b = new Map(base.map(v => [key(v), v]));
  const l = new Map(local.map(v => [key(v), v]));
  const r = new Map(remote.map(v => [key(v), v]));
  const result: unknown[] = [];
  for (const id of new Set([...r.keys(), ...l.keys(), ...b.keys()])) {
    const value = mergeFields(b.get(id), l.get(id), r.get(id));
    if (value !== undefined) result.push(value);
  }
  return result;
}

export function mergeUserValue(key: string, base: string | null, local: string | null, remote: string | null): string | null {
  if (local === base) return remote;
  if (remote === base || local === remote) return local;
  if (local === null || remote === null) return local;
  try {
    const b: unknown = base === null ? null : JSON.parse(base);
    const l: unknown = JSON.parse(local), r: unknown = JSON.parse(remote);
    let merged: unknown;
    if (Array.isArray(l) && Array.isArray(r) && (identityKeys[key] || sets.has(key))) {
      merged = mergeArray(Array.isArray(b) ? b : [], l, r, identityKeys[key] || '');
    } else if (object(l) && object(r)) {
      merged = mergeFields(b, l, r);
    } else return local;
    return JSON.stringify(merged);
  } catch { return local; }
}

// Without a baseline there is no way to distinguish an old record from a cloud deletion.
// Existing cloud values win; the caller retains an account-scoped legacy backup for explicit import.
export function mergeLegacyValue(key: string, local: string | null, remote: string | null): string | null {
  if (remote === null) return local;
  if (local === null) return remote;
  return remote;
}
